<template>
    <section v-if="backgrounds.length" class="mb-5" aria-labelledby="homepage-background-heading">
        <button
            type="button"
            class="flex w-full items-center justify-between gap-4 rounded-xl border border-gray-200 bg-slate-50 p-3 text-left transition hover:border-gray-300 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500"
            aria-haspopup="dialog"
            @click="modalOpen = true"
        >
            <div class="flex min-w-0 items-center gap-3">
                <img
                    v-if="selectedBackground"
                    :src="selectedBackground.thumbnail"
                    alt=""
                    class="h-14 w-24 shrink-0 rounded-lg object-cover"
                />
                <span
                    v-else
                    class="flex h-14 w-24 shrink-0 items-center justify-center rounded-lg bg-slate-200 text-slate-400"
                >
                    <QrIcon icon="ban" class="h-6 w-6"/>
                </span>

                <div class="min-w-0">
                    <h3 id="homepage-background-heading" class="text-sm font-semibold text-gray-900">
                        {{ trans('homepage.background') }}
                    </h3>
                    <span class="block truncate text-sm text-gray-500">
                        {{ selectedBackground?.alias ?? trans('homepage.no-background') }}
                    </span>
                </div>
            </div>

            <QrIcon icon="chevron-right" class="h-4 w-4 shrink-0 text-gray-500"/>
        </button>

        <QrModal
            v-model="modalOpen"
            huge
            :title="trans('homepage.background')"
            :close-text="trans('button.close')"
        >
            <p class="mb-4 text-sm text-gray-500">
                {{ trans('homepage.background-description') }}
            </p>

            <div class="grid max-h-[65vh] grid-cols-2 gap-3 overflow-y-auto pr-1 sm:grid-cols-3 lg:grid-cols-4">
                <button
                    type="button"
                    class="homepage-background-option overflow-hidden rounded-lg border-2 bg-white text-left transition"
                    :class="selectedBackgroundId === null ? 'border-green-500' : 'border-gray-200 hover:border-gray-300'"
                    :aria-pressed="selectedBackgroundId === null"
                    :disabled="saving"
                    @click="selectBackground(null)"
                >
                    <span class="flex aspect-video items-center justify-center bg-slate-100 text-slate-400">
                        <QrIcon icon="ban" class="h-7 w-7"/>
                    </span>
                    <span class="block truncate px-3 py-2 text-sm font-medium text-gray-900">
                        {{ trans('homepage.no-background') }}
                    </span>
                </button>

                <button
                    v-for="background in backgrounds"
                    :key="background.id"
                    type="button"
                    class="homepage-background-option overflow-hidden rounded-lg border-2 bg-white text-left transition"
                    :class="selectedBackgroundId === background.id ? 'border-green-500' : 'border-gray-200 hover:border-gray-300'"
                    :aria-label="background.alias"
                    :aria-pressed="selectedBackgroundId === background.id"
                    :disabled="saving"
                    @click="selectBackground(background.id)"
                >
                    <img
                        :src="background.thumbnail"
                        :alt="background.alias"
                        class="aspect-video w-full object-cover"
                    />
                    <span class="block truncate px-3 py-2 text-sm font-medium text-gray-900">
                        {{ background.alias }}
                    </span>
                </button>
            </div>
        </QrModal>
    </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import QrIcon from '@/components/application-ui/elements/QrIcon.vue';
import QrModal from '@/components/application-ui/dialogs/QrModal.vue';
import { trans } from '@/libs/helper.js';
import { useQrCodeHomepageStore } from '@/store/qrCodeHomepage.js';
import { useQrCodesStore } from '@/store/qrCodes.js';

const backgrounds = ref([]);
const saving = ref(false);
const selectedBackgroundId = ref(null);
const modalOpen = ref(false);

const qrCodeStore = useQrCodesStore();
const { qrCode } = storeToRefs(qrCodeStore);
const homepageStore = useQrCodeHomepageStore();

const selectedBackground = computed(() => {
    return backgrounds.value.find(background => background.id === selectedBackgroundId.value) ?? null;
});

const fetchBackgrounds = async () => {
    try {
        const { data } = await axios.get('/api/homepage-backgrounds');
        backgrounds.value = data.data;
    } catch (error) {
        backgrounds.value = [];
    }
};

const selectBackground = async (backgroundId) => {
    if (saving.value) {
        return;
    }

    if (selectedBackgroundId.value === backgroundId) {
        modalOpen.value = false;
        return;
    }

    const previousBackgroundId = selectedBackgroundId.value;
    selectedBackgroundId.value = backgroundId;
    saving.value = true;

    const updated = await homepageStore.updateBackground(backgroundId);

    if (! updated) {
        selectedBackgroundId.value = previousBackgroundId;
    } else {
        modalOpen.value = false;
    }

    saving.value = false;
};

onMounted(async () => {
    selectedBackgroundId.value = qrCode.value.homepage.homepage_background_id;
    await fetchBackgrounds();
});
</script>

<style scoped>
.homepage-background-option {
    position: relative;
}

.homepage-background-option::after {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    content: '';
    pointer-events: none;
}

.homepage-background-option:focus-visible {
    outline: none;
}

.homepage-background-option:focus-visible::after {
    box-shadow: inset 0 0 0 2px rgb(34 197 94);
}
</style>
